import { ChannelForwardChannelSelector } from "components/ChannelForwardChannelSelector/ChannelForwardChannelSelector";
import { ChannelHeader } from "components/ChannelHeader/ChannelHeader";
import { ChannelHeaderWithOnlySwitch } from "components/ChannelHeaderWithOnlySwitch/ChannelHeaderWithOnlySwitch";
import { ChannelMediaSlider } from "components/ChannelMediaSlider/ChannelMediaSlider";
import { ChannelMessageSearch } from "components/ChannelMessageSearch/ChannelMessageSearch";
import { ChatActionCentreSlider } from "components/ChannelinfoSlider/ChannelInfo/ActionCentre/ChatActionCentreSlider";
import { ChatTicketSlider } from "components/ChannelinfoSlider/ChannelInfo/ChatTickets/ChatTicketsSlider";
import { ChannelPendingActions } from "components/ChannelinfoSlider/ChannelPendingActions";
import { ChannelPinnedItem } from "components/ChannelinfoSlider/ChannelPinnedItem";
import { ChannelInfoSlider } from "components/ChannelinfoSlider/ChannelinfoSlider";
import { Button } from "components/DesignSystem/Button/Button";
import { MessageInfoSlider } from "components/WhatsappSharedSlider/MessageInfoSlider";
import { WhatsappForwardedSlider } from "components/WhatsappSharedSlider/WhatsappForwardedSlider";
import { Channel } from "components/chat/Channel";
import { ChannelList } from "components/chat/ChannelList";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { AnimatedSlide } from "components/design/AnimatedSlide";
import { ArrowLeft } from "components/icons/ArrowLeft";
import { BIG } from "constants/chatBubbleWidths";
import { withChatFilterContext } from "contexts/ChatFilterContext";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { ShowControl } from "hooks/useShow";
import { useStreamFilterQuery } from "hooks/useStreamFilterQuery";
import React, { useMemo } from "react";
import { useChatContext } from "stream-chat-react";

type ChatProps = {
  channelId?: string | null;
  setChannelId(value: string | null): void;
  messageId?: string;
  header?: React.ReactNode;
  headerWithOnlySwitch?: boolean;
  actionsAvailable?: boolean;
  filter?: object;
  hideHeaders?: boolean;
  canGoback?: boolean;
  hideChannelCheckboxSelect?: boolean;
  customBackButton?: React.ReactNode | null;
  disableAutoFocus?: boolean;
  // Shows the channel list beside the opened channel instead of one at a time
  splitView?: boolean;
} & (
  | { hidable: true; hideControl: ShowControl }
  | { hidable?: false; hideControl?: ShowControl }
);

const Chat = ({
  channelId,
  setChannelId,
  messageId,
  header,
  headerWithOnlySwitch,
  actionsAvailable = false,
  hidable,
  hideControl,
  filter: filterFromProps,
  hideHeaders,
  canGoback = true,
  hideChannelCheckboxSelect,
  customBackButton = null,
  disableAutoFocus,
  splitView = false,
}: ChatProps) => {
  const filterQuery = useStreamFilterQuery();
  const { client } = useChatContext();
  const isSmallScreen = useIsSmallScreen();
  // Small screens stay single pane, there isn't room for both
  const isSplitView = splitView && !isSmallScreen;

  const filter = useMemo(
    () => ({
      type: "messaging",
      members: { $in: [client.userID!] },
    }),
    [client.userID]
  );

  const channelList = (
    <ChannelList
      channelId={channelId || undefined}
      onSelect={(channelId) => setChannelId(channelId)}
      filters={{ ...filter, ...filterFromProps, ...filterQuery }}
      hideHeaders={hideHeaders}
      hideChannelCheckboxSelect={hideChannelCheckboxSelect}
    />
  );

  const channel = (
    <Channel
      disableAutoFocus={disableAutoFocus}
      Header={(props) =>
        headerWithOnlySwitch ? (
          <ChannelHeaderWithOnlySwitch
            {...props}
            {...(hidable ? { hidable: true, hideControl } : { hidable: false })}
            channelId={channelId}
          />
        ) : (
          <ChannelHeader
            {...props}
            actionsAvailable={actionsAvailable}
            backButton={
              canGoback && !isSplitView ? (
                <Button
                  size="small"
                  customType="ghost_icon"
                  onClick={() => setChannelId(null)}
                >
                  <ArrowLeft size="16px" />
                </Button>
              ) : (
                customBackButton
              )
            }
          />
        )
      }
      channelId={channelId || undefined}
      messageId={messageId}
      messageMaxWidth={isSplitView ? BIG : "360px"}
    >
      <ChannelMessageSearch
        block={!isSplitView}
        onSelect={(channelId) => setChannelId(channelId)}
      />
      <ChannelInfoSlider block={!isSplitView} />
      <ChannelPendingActions block={!isSplitView} />
      <ChannelPinnedItem block={!isSplitView} />
      <ChannelMediaSlider block={!isSplitView} />
      <WhatsappForwardedSlider block={!isSplitView} />
      <MessageInfoSlider block={!isSplitView} />
      <ChannelForwardChannelSelector />
      <ChatTicketSlider block={!isSplitView} />
      <ChatActionCentreSlider block={!isSplitView} />
    </Channel>
  );

  return (
    <DashboardContainer className="t-border-0 t-border-l t-border-solid t-border-neutral-0 t-w-full t-h-full t-overflow-hidden">
      {header && (
        <DashboardContainer.Header>{header}</DashboardContainer.Header>
      )}
      {isSplitView ? (
        <DashboardContainer.Content className="t-w-full t-bg-white t-h-full t-relative !t-overflow-hidden">
          <div className="t-flex t-h-full t-w-full">
            <div className="t-w-[360px] t-shrink-0 t-h-full t-overflow-hidden t-border-0 t-border-r t-border-solid t-border-neutral-10">
              {channelList}
            </div>
            <div className="t-min-w-0 t-flex-1 t-h-full">{channel}</div>
          </div>
        </DashboardContainer.Content>
      ) : (
        <DashboardContainer.Content className="t-w-full t-overflow-y-auto t-bg-white t-h-full t-relative t-overflow-x-hidden">
          <AnimatedSlide delay={0.1} isVisible={!channelId} direction="left">
            {channelList}
          </AnimatedSlide>

          <AnimatedSlide
            delay={0.1}
            isVisible={Boolean(channelId)}
            direction="right"
          >
            {channelId && channel}
          </AnimatedSlide>
        </DashboardContainer.Content>
      )}
    </DashboardContainer>
  );
};

export const ChatPane = withChatFilterContext(Chat);
